import React from 'react';

import useStyles from './useStyles';

import { Breadcrumbs, Checkbox } from '@material-ui/core';
import { CustomizedTypography } from '@components/Customized';
import { ChevronRightOutlined } from '@material-ui/icons';
import { FolderIcon } from './assets';
import { ConnectDriveListProps } from './types';
import { formatDate, formatFileSize } from '@utils/formatter';
import AppConfig from '@config';
import { clsx } from '@components/Provider';
import {
  castingRegex,
  cncRegex,
  imageRegex,
  moldingRegex,
  printingRegex,
  sheetMetalRegex
} from '@utils/regex';
import { Image } from '@components/Image';
import { CapaQuotationServiceType } from '@generated/graphql';

const ConnectDriveList = ({
  matches,
  files,
  folders,
  selectedFile,
  serviceType,
  onSelect,
  onChangePath
}: ConnectDriveListProps) => {
  const classes = useStyles();
  const isEmpty = files.length === 0;

  const findImage = (fileName: string) => !!imageRegex.exec(fileName);
  const findMatchedFile = (fileName: string) => {
    if (serviceType === CapaQuotationServiceType.Cnc) {
      return !!cncRegex.exec(fileName);
    }

    if (serviceType === CapaQuotationServiceType.Printing) {
      return !!printingRegex.exec(fileName);
    }

    if (serviceType === CapaQuotationServiceType.Inj) {
      return !!moldingRegex.exec(fileName);
    }

    if (serviceType === CapaQuotationServiceType.SheetMetal) {
      return !!sheetMetalRegex.exec(fileName);
    }

    if (serviceType === CapaQuotationServiceType.Casting) {
      return !!castingRegex.exec(fileName);
    }

    return false;
  };

  return (
    <div className={classes['Connect-Drive-List-Root']}>
      <div className="Connect-Drive-List-Search"></div>

      <Breadcrumbs separator={<ChevronRightOutlined />} aria-label="breadcrumb">
        {folders.map((folder, key) => {
          const isSelected = folders.some(
            (selected) => selected.folderName === folder.folderName
          );

          return (
            <span
              key={key}
              className={clsx('breadcrumb-item', {
                selected: isSelected && folder.folderName !== '/'
              })}
              onClick={() => onChangePath(folder.id ?? '')}
            >
              {folder.folderName === '/' ? '내 드라이브' : folder.folderName}
            </span>
          );
        })}
      </Breadcrumbs>

      {!matches && (
        <div className="list-header">
          <CustomizedTypography
            variant="body2"
            color="textPrimary"
            className="list-header-fileName"
          >
            이름
          </CustomizedTypography>
          <CustomizedTypography
            variant="body2"
            color="textPrimary"
            className="list-header-uploadedAt"
          >
            업로드 시간
          </CustomizedTypography>
          <CustomizedTypography
            variant="body2"
            color="textPrimary"
            className="list-header-fileSize"
          >
            크기
          </CustomizedTypography>
        </div>
      )}

      {isEmpty && (
        <div className="list-empty">
          <CustomizedTypography variant="body2" color="textPrimary">
            파일이 없습니다.
          </CustomizedTypography>
        </div>
      )}

      {!isEmpty && (
        <div className="list-container">
          {files.map((file, key) => {
            const isSelected = selectedFile.some(
              (selected) => selected.fileId === file.fileId
            );

            const isImage = findImage(file.fileName ?? '');
            const isMatch = findMatchedFile(file.fileName);

            return (
              <div
                key={key}
                className={clsx('list-items', {
                  selected: isSelected,
                  notMatch: !isMatch && file.isFile
                })}
                onClick={() => {
                  if (file.isFile) {
                    if (!isMatch) return;

                    onSelect(file);
                  } else {
                    return onChangePath(file.id ?? '');
                  }
                }}
              >
                <div className="group">
                  {!matches && (
                    <div className="checkbox">
                      {file.isFile && (
                        <Checkbox
                          disabled={!isMatch}
                          color="primary"
                          checked={isSelected}
                          onChange={(e, checked) => {
                            onSelect(file);
                          }}
                        />
                      )}
                    </div>
                  )}
                  <div className="thumbnail">
                    <Image
                      src={
                        file.isFile
                          ? `${AppConfig.hoopsViewerAPI}/${file.fileId}.${
                              isImage ? file.originalFileExt : 'png'
                            }`
                          : FolderIcon
                      }
                      alt=""
                    />
                  </div>
                  {matches ? (
                    <div className="mobile_group">
                      <CustomizedTypography
                        variant="body2"
                        color="textPrimary"
                        className="fileName"
                      >
                        {file.fileName}
                      </CustomizedTypography>
                      <CustomizedTypography
                        variant="body2"
                        color="textPrimary"
                        className="fileName"
                      >
                        {file.createdAt
                          ? formatDate(file.createdAt, 'MM월DD일')
                          : ''}
                        {file.isFile && ' · '}
                        {file.fileSize ? formatFileSize(file.fileSize) : ''}
                      </CustomizedTypography>
                    </div>
                  ) : (
                    <CustomizedTypography
                      variant="body2"
                      color="textPrimary"
                      className="fileName"
                    >
                      {file.fileName}
                    </CustomizedTypography>
                  )}
                </div>

                {!matches ? (
                  <>
                    <CustomizedTypography
                      variant="body2"
                      color="textPrimary"
                      className="uploadedAt"
                    >
                      {file.createdAt
                        ? formatDate(file.createdAt, 'MM월DD일')
                        : ''}
                    </CustomizedTypography>
                    <CustomizedTypography
                      variant="body2"
                      color="textPrimary"
                      className="fileSize"
                    >
                      {file.fileSize ? formatFileSize(file.fileSize) : ''}
                    </CustomizedTypography>
                  </>
                ) : (
                  <div className="checkbox">
                    {file.isFile && (
                      <Checkbox
                        disabled={!isMatch}
                        color="primary"
                        checked={isSelected}
                        onChange={(e, checked) => {
                          onSelect(file);
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ConnectDriveList;
